import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap, catchError, map } from 'rxjs';
import { environment } from '../environments/environment';
import { LoginPayload, AuthResponse, ResetPasswordPayload } from '../types';
import { SKIP_AUTH_ERROR_REDIRECT } from '../helpers/http-context';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private baseUrl = environment.apiUrl;
  private sessionChecked = false;
  private sessionValid = false;

  constructor(private http: HttpClient, private router: Router) {}

  requestPasswordReset(emailOrUsername: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/auth/forgot-password`, {
      emailOrUsername: emailOrUsername.trim(),
    });
  }

  resetPassword(payload: ResetPasswordPayload): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/auth/reset-password`, payload);
  }

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/login`, payload).pipe(
      tap((response: any) => {
        const res = response.data;

        const roles: string[] = res.roles || [];

        if (roles.includes('student')) {
          throw new Error(
            'Students are not allowed to access this portal. Please use the official mobile app'
          );
        }

        localStorage.setItem('access_token', res.accessToken);
        localStorage.setItem('refresh_token', res.refreshToken);
        localStorage.setItem('chipo_user', JSON.stringify(res.user));
        this.sessionChecked = true;
        this.sessionValid = true;
      })
    );
  }

  logout(): void {
    this.http.post(`${this.baseUrl}/auth/logout`, {}).subscribe({
      complete: () => this.clearSessionAndRedirect(),
      error: () => this.clearSessionAndRedirect(),
    });
  }

  clearSessionLocally(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('chipo_user');
    this.sessionChecked = true;
    this.sessionValid = false;
  }

  clearSessionAndRedirect(returnUrl?: string): void {
    this.clearSessionLocally();
    const queryParams = returnUrl ? { returnUrl } : undefined;
    void this.router.navigate(['/login'], { queryParams });
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getUser(): AuthResponse['user'] | null {
    const raw = localStorage.getItem('chipo_user');
    if (!raw || raw === 'undefined' || raw === 'null') {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      localStorage.removeItem('chipo_user');
      return null;
    }
  }

  getRoles(): string[] {
    const raw = localStorage.getItem('chipo_user');

    if (!raw || raw === 'undefined' || raw === 'null') return [];

    try {
      const user = JSON.parse(raw);
      return user?.roles ?? [];
    } catch {
      return [];
    }
  }

  getNormalizedRoles(): string[] {
    const roles = this.getRoles();

    return roles.map((r) => {
      switch (r.toLowerCase()) {
        case 'organization_admin':
          return 'admin';
        case 'super_admin':
          return 'super_admin';
        case 'student':
          return 'student';
        case 'teacher':
          return 'teacher';
        default:
          return r;
      }
    });
  }

  hasValidSession(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  isSessionValidated(): boolean {
    return this.sessionChecked;
  }

  isSessionValid(): boolean {
    return this.sessionValid;
  }

  /** Validates token with the API; uses existing splash while resolving. */
  validateSession(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      this.sessionChecked = true;
      this.sessionValid = false;
      return of(false);
    }

    if (this.isTokenExpired(token)) {
      this.clearSessionLocally();
      return of(false);
    }

    if (this.sessionChecked && this.sessionValid) {
      return of(true);
    }

    return this.http
      .get(`${this.baseUrl}/auth/me`, {
        context: new HttpContext().set(SKIP_AUTH_ERROR_REDIRECT, true),
      })
      .pipe(
        map(() => {
          this.sessionChecked = true;
          this.sessionValid = true;
          return true;
        }),
        catchError((err) => {
          if (err?.status === 401) {
            this.clearSessionLocally();
            return of(false);
          }

          // Endpoint unavailable — trust local JWT expiry only
          const stillValid = !this.isTokenExpired(token);
          this.sessionChecked = true;
          this.sessionValid = stillValid;
          if (!stillValid) {
            this.clearSessionLocally();
          }
          return of(stillValid);
        })
      );
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload?.exp) return false;
      return Date.now() >= payload.exp * 1000;
    } catch {
      return false;
    }
  }
}
